// Direct Worker Assignment — a supervisor picks Workers for a Shift as part of
// publishing it (Publish and Notify) or of posting coverage (requestAction).

// 0 = full Shift, 1 / 2 = first / second half, 3 = custom flexi window
export type DirectAssignmentSlot = 0 | 1 | 2 | 3;

export interface WorkerSlot {
	workerId: string;
	slot: DirectAssignmentSlot;
	// Required when slot is 3
	workerShiftStartDateTime?: string;
	workerShiftEndDateTime?: string;
}

// A picked Worker as the dialogs keep it. The name is for the results screen
// only and is stripped before the request is sent.
export interface SelectedWorkerSlot extends WorkerSlot {
	workerName: string;
}

export interface DirectAssignment {
	shiftId: number;
	workerSlots: WorkerSlot[];
}

export interface DirectAssignmentResult {
	shiftId: number;
	// Null when the whole Shift's selection was rejected rather than one Worker
	workerId: string | null;
	isSuccess: boolean;
	// Free text, already in the user's language. Set only when isSuccess is false
	reason: string | null;
}

export interface PublishShiftsPayload {
	shiftIds: number[];
	directAssignments: DirectAssignment[];
}

export const toWorkerSlots = (selection: SelectedWorkerSlot[]): WorkerSlot[] =>
	selection.map(
		({ workerId, slot, workerShiftStartDateTime, workerShiftEndDateTime }) => ({
			workerId,
			slot,
			...(slot === 3
				? { workerShiftStartDateTime, workerShiftEndDateTime }
				: {}),
		})
	);

// The part of a Shift that decides how many Workers it can take
export interface DirectAssignmentCapacity {
	startDateTime: Date | string;
	endDateTime: Date | string;
	numberOfWorkersNeeded: number | string;
}

const MINUTE_MS = 60 * 1000;

// Same midpoint the API's signup step uses for halves: whole minutes, rounded
// down
const getShiftMidpoint = (shift: DirectAssignmentCapacity) => {
	const start = new Date(shift.startDateTime).getTime();
	const totalMinutes = Math.floor(
		(new Date(shift.endDateTime).getTime() - start) / MINUTE_MS
	);
	return start + Math.floor(totalMinutes / 2) * MINUTE_MS;
};

// When a picked Worker is on the Shift, or null for a custom window with no
// times yet, which holds no time
const getWorkerWindow = (
	{ slot, workerShiftStartDateTime, workerShiftEndDateTime }: WorkerSlot,
	shift: DirectAssignmentCapacity
) => {
	const start = new Date(shift.startDateTime).getTime();
	const end = new Date(shift.endDateTime).getTime();
	const midpoint = getShiftMidpoint(shift);
	if (slot === 1) return { start, end: midpoint };
	if (slot === 2) return { start: midpoint, end };
	if (slot === 3) {
		return workerShiftStartDateTime && workerShiftEndDateTime
			? {
				start: new Date(workerShiftStartDateTime).getTime(),
				end: new Date(workerShiftEndDateTime).getTime(),
			}
			: null;
	}
	return { start, end };
};

// How many Workers each half of a Shift holds. A full Shift holds both halves
// and a custom window holds every half it reaches into. The picker opens a
// half by this; it cannot tell where inside a half a spot is free.
export const countHalvesTaken = (
	selection: WorkerSlot[],
	shift: DirectAssignmentCapacity
) => {
	const midpoint = getShiftMidpoint(shift);
	return selection.reduce(
		(taken, workerSlot) => {
			const window = getWorkerWindow(workerSlot, shift);
			if (!window) return taken;
			return {
				firstHalf: taken.firstHalf + (window.start < midpoint ? 1 : 0),
				secondHalf: taken.secondHalf + (window.end > midpoint ? 1 : 0),
			};
		},
		{ firstHalf: 0, secondHalf: 0 }
	);
};

// The most picked Workers on the Shift at any one moment. The API caps a pick
// by this (GAT-8016), so one Worker per half, or back-to-back custom windows,
// fits a 1-Worker Shift.
export const getPeakSimultaneousWorkers = (
	selection: WorkerSlot[],
	shift: DirectAssignmentCapacity
) => {
	const events = selection.flatMap((workerSlot) => {
		const window = getWorkerWindow(workerSlot, shift);
		return window
			? [
				{ at: window.start, change: 1 },
				{ at: window.end, change: -1 },
			]
			: [];
	});
	// Ends before starts at the same moment, so 08-12 and 12-16 do not overlap
	events.sort((a, b) => a.at - b.at || a.change - b.change);
	let running = 0;
	return events.reduce((peak, { change }) => {
		running += change;
		return Math.max(peak, running);
	}, 0);
};

// How far a Worker's custom window can be stretched, one step at a time, before
// more than Required Workers would be on the Shift at once. Everyone else on
// the Shift keeps their time, so a window can run up to the next Worker's
// start however short the gap between them is. A window that already breaks
// the limit can only shrink.
export const getCustomWindowBounds = (
	workerId: string,
	window: { start: Date; end: Date },
	occupied: WorkerSlot[],
	shift: DirectAssignmentCapacity,
	stepMinutes: number
) => {
	const others = occupied.filter(
		(workerSlot) => String(workerSlot.workerId) !== String(workerId)
	);
	const fits = (start: number, end: number) =>
		isWithinRequiredWorkers(
			[
				...others,
				{
					workerId,
					slot: 3,
					workerShiftStartDateTime: new Date(start).toISOString(),
					workerShiftEndDateTime: new Date(end).toISOString(),
				},
			],
			shift
		);
	const step = stepMinutes * MINUTE_MS;
	const shiftStart = new Date(shift.startDateTime).getTime();
	const shiftEnd = new Date(shift.endDateTime).getTime();
	const start = window.start.getTime();
	const end = window.end.getTime();

	let minStart = start;
	while (minStart - step >= shiftStart && fits(minStart - step, end)) {
		minStart -= step;
	}
	let maxEnd = end;
	while (maxEnd + step <= shiftEnd && fits(start, maxEnd + step)) {
		maxEnd += step;
	}
	return { minStart: new Date(minStart), maxEnd: new Date(maxEnd) };
};

export const isWithinRequiredWorkers = (
	selection: WorkerSlot[],
	shift: DirectAssignmentCapacity
) =>
	getPeakSimultaneousWorkers(selection, shift) <=
	Number(shift.numberOfWorkersNeeded);

export interface DirectAssignmentFill {
	slotsFilled: number;
	fullSlots?: number;
	firstHalfSlots?: number;
	secondHalfSlots?: number;
}

// What a Shift holds once its picked Workers are on it, in the fields the
// Shift API reports after a reload. slotsFilled is the peak at any one moment,
// so one Worker per half fills one spot rather than two. A custom window does
// not map onto halves, so with one in the pick the half counts are left unset
// and the Shift card and Accepted fall back to slotsFilled.
export const getDirectAssignmentFill = (
	workerSlots: WorkerSlot[],
	results: DirectAssignmentResult[],
	shift: DirectAssignmentCapacity
): DirectAssignmentFill => {
	const assignedWorkerIds = new Set(
		results
			.filter((result) => result.isSuccess && !!result.workerId)
			.map((result) => result.workerId)
	);
	const assigned = workerSlots.filter((workerSlot) =>
		assignedWorkerIds.has(workerSlot.workerId)
	);
	const slotsFilled = getPeakSimultaneousWorkers(assigned, shift);
	if (assigned.some((workerSlot) => workerSlot.slot === 3)) {
		return {
			slotsFilled,
			fullSlots: undefined,
			firstHalfSlots: undefined,
			secondHalfSlots: undefined,
		};
	}
	const countSlot = (slot: DirectAssignmentSlot) =>
		assigned.filter((workerSlot) => workerSlot.slot === slot).length;
	return {
		slotsFilled,
		fullSlots: countSlot(0),
		firstHalfSlots: countSlot(1),
		secondHalfSlots: countSlot(2),
	};
};

// Publish returns one entry per Shift; requestAction returns the list on the
// body. Either way, pull out every result regardless of where it sits.
export const collectDirectAssignmentResults = (
	data: unknown
): DirectAssignmentResult[] => {
	const entries = Array.isArray(data) ? data : [data];
	return entries.flatMap((entry) =>
		entry && typeof entry === "object" &&
		Array.isArray((entry as { directAssignmentResults?: unknown })
			.directAssignmentResults)
			? ((entry as { directAssignmentResults: DirectAssignmentResult[] })
				.directAssignmentResults)
			: []
	);
};
